import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lands the user after Emergent-managed Google OAuth.
 * URL fragment: #session_id=...
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError('Missing session_id in callback URL.');
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google`, {
          session_id: sessionId,
        });
        const { token, user } = res.data;
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        toast.success(`Welcome, ${user?.name || 'there'}!`);
        // Strip fragment then go to dashboard so AuthContext picks the new token up.
        window.history.replaceState(null, '', '/');
        window.location.href = user?.role === 'recruiter' ? '/command-center' : '/dashboard';
      } catch (err) {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Google sign-in failed. Please try again.';
        setError(msg);
        toast.error(msg);
      }
    })();
  }, [navigate]);

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6"
      data-testid="auth-callback-page"
    >
      <div className="text-center max-w-md">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-6 h-6 text-slate-900" />
        </div>
        {!error ? (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-teal-400" />
            <p className="text-sm text-slate-300">Finishing sign-in…</p>
          </>
        ) : (
          <>
            <p className="text-sm text-red-400 mb-3" data-testid="auth-callback-error">
              {error}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-teal-400 hover:underline"
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
